import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { getQuestions, addQuestion, deleteQuestion, bulkDeleteQuestions, uploadBulkQuestions } from '../../api/questionsApi';
import { FaPlus, FaTrash, FaCopy, FaImage, FaFileImport, FaDownload } from 'react-icons/fa';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const QuestionBankPage = () => {
  const { register, handleSubmit, reset } = useForm();
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questionType, setQuestionType] = useState('MCQ');
  const [options, setOptions] = useState([{ id: 1, text: '', isCorrect: false }, { id: 2, text: '', isCorrect: false }]);
  // Coding question settings (type = CODING)
  const emptyTestCase = () => ({ id: Date.now() + Math.random(), input: '', expectedOutput: '', isHidden: false, weight: 1 });
  const [codingLanguages, setCodingLanguages] = useState(['python', 'java', 'cpp', 'c', 'javascript']);
  const [timeLimitSeconds, setTimeLimitSeconds] = useState(2);
  const [memoryLimitMb, setMemoryLimitMb] = useState(256);
  const [testCases, setTestCases] = useState([emptyTestCase()]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const res = await getQuestions();
      setQuestions(res.data);
      setSelectedQuestionIds(prev => prev.filter(id => res.data?.some(q => q.id === id)));
    } catch (err) {
      console.error('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const addOption = () => {
    setOptions([...options, { id: Date.now(), text: '', isCorrect: false }]);
  };

  const removeOption = (id) => {
    setOptions(options.filter(o => o.id !== id));
  };

  const handleOptionChange = (id, field, value) => {
    setOptions(options.map(o => {
      if (o.id === id) {
        return { ...o, [field]: value };
      }
      if (field === 'isCorrect' && value === true && questionType === 'MCQ') {
        return { ...o, isCorrect: false };
      }
      return o;
    }));
  };

  const CODING_LANGUAGE_OPTIONS = [
    { key: 'python', label: 'Python 3' },
    { key: 'java', label: 'Java' },
    { key: 'cpp', label: 'C++' },
    { key: 'c', label: 'C' },
    { key: 'javascript', label: 'JavaScript' }
  ];

  const toggleLanguage = (key) => {
    setCodingLanguages(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const updateTestCase = (id, field, value) => {
    setTestCases(prev => prev.map(t => (t.id === id ? { ...t, [field]: value } : t)));
  };

  const onSubmit = async (data) => {
    if (questionType === 'CODING') {
      if (codingLanguages.length === 0) return showCustomAlert('Select at least one allowed language');
      if (testCases.length === 0 || testCases.some(t => t.expectedOutput.trim() === '')) {
        return showCustomAlert('Add at least one test case, and give every test case an expected output');
      }
      if (!testCases.some(t => !t.isHidden)) {
        return showCustomAlert('Keep at least one test case visible (sample) so students can try their code with Run');
      }
    }
    setSaving(true);
    const newQuestion = {
      type: questionType,
      text: data.questionText,
      marks: Number(data.marks),
      negativeMarks: Number(data.negativeMarks),
      difficulty: data.difficulty,
      category: data.category,
      options: questionType === 'CODING' ? [] : options.map(({ text, isCorrect }) => ({ text, isCorrect })),
      ...(questionType === 'CODING'
        ? {
            codingLanguages,
            timeLimitSeconds: Number(timeLimitSeconds),
            memoryLimitMb: Number(memoryLimitMb),
            testCases: testCases.map(({ input, expectedOutput, isHidden, weight }) => ({ input, expectedOutput, isHidden, weight: Number(weight) || 1 }))
          }
        : {})
    };

    try {
      await addQuestion(newQuestion);
      showCustomAlert('Question added to bank!', () => {
        reset();
        setOptions([{ id: 1, text: '', isCorrect: false }, { id: 2, text: '', isCorrect: false }]);
        setTestCases([emptyTestCase()]);
        fetchQuestions();
      });
    } catch (err) {
      console.error(err);
      showCustomAlert(err.response?.data?.message || 'Failed to add question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await deleteQuestion(id);
        setSelectedQuestionIds(prev => prev.filter(qId => qId !== id));
        fetchQuestions();
      } catch (err) {
        console.error(err);
        showCustomAlert(err.response?.data?.message || 'Failed to delete question');
      }
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedQuestionIds(prev =>
      prev.includes(id) ? prev.filter(qId => qId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (questions.length === 0) return;
    if (selectedQuestionIds.length === questions.length) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(questions.map(q => q.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestionIds.length === 0) return;
    const count = selectedQuestionIds.length;
    if (window.confirm(`Are you sure you want to delete ${count} selected question${count > 1 ? 's' : ''}?`)) {
      setDeletingBulk(true);
      try {
        await bulkDeleteQuestions(selectedQuestionIds);
        showCustomAlert(`Successfully deleted ${count} question${count > 1 ? 's' : ''}.`, () => {
          setSelectedQuestionIds([]);
          fetchQuestions();
        });
      } catch (err) {
        console.error(err);
        showCustomAlert(err.response?.data?.message || 'Failed to delete selected questions');
      } finally {
        setDeletingBulk(false);
      }
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      showCustomAlert('Please select a CSV file first');
      return;
    }
    setUploadingBulk(true);
    setBulkErrors([]);
    const formData = new FormData();
    formData.append('file', bulkFile);
    try {
      const res = await uploadBulkQuestions(formData);
      const data = res.data;
      showCustomAlert(`Upload Summary:\nUploaded: ${data.uploaded}\nImported: ${data.imported}\nSkipped: ${data.skipped}`, () => {
        if (data.errors && data.errors.length > 0) {
          setBulkErrors(data.errors);
        } else {
          setShowBulkModal(false);
          setBulkFile(null);
        }
        fetchQuestions();
      });
    } catch (err) {
      showCustomAlert(err.response?.data?.message || 'Bulk upload failed');
    } finally {
      setUploadingBulk(false);
    }
  };

  const downloadSampleTemplate = () => {
    const csvContent = "data:text/csv;charset=utf-8,type,text,marks,negativeMarks,difficulty,category,option1,option2,option3,option4,correctOption\nMCQ,What is 2+2?,4,1,easy,Math,2,3,4,5,3\nMCQ,Capital of France?,4,1,easy,Geography,London,Paris,Berlin,Rome,2";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "questions_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Template with one MCQ row and coding rows (languages, limits and up to 5 test cases; add tc6_..., tc7_... for more)
  const downloadCodingTemplate = () => {
    const header = ['type', 'text', 'marks', 'negativeMarks', 'difficulty', 'category', 'option1', 'option2', 'option3', 'option4', 'correctOption', 'languages', 'timeLimitSeconds', 'memoryLimitMb'];
    for (let i = 1; i <= 5; i++) header.push(`tc${i}_input`, `tc${i}_output`, `tc${i}_hidden`, `tc${i}_weight`);
    const mk = (vals) => header.map(h => (vals[h] === undefined ? '' : vals[h]));
    const tcs = (list) => {
      const o = {};
      list.forEach(([input, output, hidden, weight], idx) => {
        o[`tc${idx + 1}_input`] = input; o[`tc${idx + 1}_output`] = output; o[`tc${idx + 1}_hidden`] = hidden; o[`tc${idx + 1}_weight`] = weight;
      });
      return o;
    };
    const rows = [
      mk({ type: 'MCQ', text: 'What is 2+2?', marks: 4, negativeMarks: 1, difficulty: 'easy', category: 'Math', option1: '3', option2: '4', option3: '5', option4: '6', correctOption: 2 }),
      mk({
        type: 'CODING', text: 'Sum of Two Numbers\nRead two integers, one per line, and print their sum.', marks: 10, negativeMarks: 0, difficulty: 'easy', category: 'Coding',
        languages: 'python,java,cpp,c,javascript', timeLimitSeconds: 2, memoryLimitMb: 256,
        ...tcs([['3\n5', '8', 'no', 1], ['10\n20', '30', 'no', 1], ['-4\n9', '5', 'yes', 1], ['0\n0', '0', 'yes', 1], ['1000000\n2000000', '3000000', 'yes', 2]])
      }),
      mk({
        type: 'CODING', text: 'Reverse a String\nRead a string and print it reversed.', marks: 10, negativeMarks: 0, difficulty: 'easy', category: 'Coding',
        languages: 'python,cpp', timeLimitSeconds: 2, memoryLimitMb: 256,
        ...tcs([['hello', 'olleh', 'no', 1], ['Blive', 'evilB', 'no', 1], ['a', 'a', 'yes', 1], ['racecar', 'racecar', 'yes', 1]])
      })
    ];
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'coding_questions_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Question Bank</h3>
          <p className="text-muted mb-0">Create and manage assessment questions.</p>
        </div>
        <button 
          className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill"
          onClick={() => setShowBulkModal(true)}
        >
          <FaFileImport /> Bulk Import Questions
        </button>
      </div>

      <div className="row g-4">
        {/* Question Builder */}
        <div className="col-lg-8">
          <div className="card custom-card border-0 shadow-sm rounded-4">
            <div className="card-body p-4 p-md-5">
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <h5 className="fw-bold m-0 text-primary-custom">Question Builder</h5>
                <select className="form-select w-auto" value={questionType} onChange={(e) => setQuestionType(e.target.value)}>
                  <option value="MCQ">Multiple Choice (Single Correct)</option>
                  <option value="MULTI">Multiple Choice (Multiple Correct)</option>
                  <option value="TF">True / False</option>
                  <option value="CODING">Coding (code editor + test cases)</option>
                </select>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                  <label className="form-label fw-semibold">Question Text *</label>
                  <textarea className="form-control p-3" rows="4" {...register('questionText', { required: true })} placeholder="Enter your question here..."></textarea>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-success">Marks (+)</label>
                    <input type="number" className="form-control p-2" defaultValue="4" {...register('marks')} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-danger">Negative Marks (-)</label>
                    <input type="number" className="form-control p-2" defaultValue="0" min="0" step="any" {...register('negativeMarks')} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Difficulty</label>
                    <select className="form-select p-2" {...register('difficulty')}>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold">Category</label>
                    <input type="text" className="form-control p-2" placeholder="e.g. Aptitude" {...register('category')} />
                  </div>
                </div>

                {questionType === 'CODING' && (
                  <div className="mb-4 p-4 bg-light rounded-4 border">
                    <h6 className="fw-bold mb-3">Coding settings</h6>

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Allowed languages</label>
                      <div className="d-flex flex-wrap gap-3">
                        {CODING_LANGUAGE_OPTIONS.map(l => (
                          <div key={l.key} className="form-check">
                            <input className="form-check-input" type="checkbox" id={`lang-${l.key}`} checked={codingLanguages.includes(l.key)} onChange={() => toggleLanguage(l.key)} />
                            <label className="form-check-label" htmlFor={`lang-${l.key}`}>{l.label}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold small">Time limit (seconds)</label>
                        <input type="number" className="form-control" min="0.5" max="15" step="0.5" value={timeLimitSeconds} onChange={(e) => setTimeLimitSeconds(e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold small">Memory limit (MB)</label>
                        <input type="number" className="form-control" min="32" max="512" value={memoryLimitMb} onChange={(e) => setMemoryLimitMb(e.target.value)} />
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-semibold small mb-0">Test cases (marks are split by weight; hidden cases are not shown to students)</label>
                      <button type="button" className="btn btn-sm btn-outline-primary rounded-pill d-flex align-items-center gap-1" onClick={() => setTestCases(prev => [...prev, emptyTestCase()])}>
                        <FaPlus /> Add Test Case
                      </button>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {testCases.map((t, i) => (
                        <div key={t.id} className="bg-white border rounded p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold small">Test case {i + 1}</span>
                            <div className="d-flex align-items-center gap-3">
                              <div className="form-check mb-0">
                                <input className="form-check-input" type="checkbox" id={`hidden-${t.id}`} checked={t.isHidden} onChange={(e) => updateTestCase(t.id, 'isHidden', e.target.checked)} />
                                <label className="form-check-label small" htmlFor={`hidden-${t.id}`}>Hidden</label>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <label className="small text-muted mb-0">Weight</label>
                                <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min="0.1" step="any" value={t.weight} onChange={(e) => updateTestCase(t.id, 'weight', e.target.value)} />
                              </div>
                              {testCases.length > 1 && (
                                <button type="button" className="btn btn-light text-danger btn-sm" onClick={() => setTestCases(prev => prev.filter(x => x.id !== t.id))}><FaTrash /></button>
                              )}
                            </div>
                          </div>
                          <div className="row g-2">
                            <div className="col-md-6">
                              <textarea className="form-control" rows="3" placeholder="Input (stdin)" style={{ fontFamily: 'monospace' }} value={t.input} onChange={(e) => updateTestCase(t.id, 'input', e.target.value)} />
                            </div>
                            <div className="col-md-6">
                              <textarea className="form-control" rows="3" placeholder="Expected output *" style={{ fontFamily: 'monospace' }} value={t.expectedOutput} onChange={(e) => updateTestCase(t.id, 'expectedOutput', e.target.value)} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Options Area */}
                {questionType !== 'CODING' && (
                <div className="mb-4 p-4 bg-light rounded-4 border">
                  <h6 className="fw-bold mb-3 d-flex justify-content-between align-items-center">
                    Options
                    {questionType !== 'TF' && (
                      <button type="button" className="btn btn-sm btn-outline-primary rounded-pill d-flex align-items-center gap-1" onClick={addOption}>
                        <FaPlus /> Add Option
                      </button>
                    )}
                  </h6>

                  {questionType === 'TF' ? (
                    <div className="d-flex flex-column gap-3">
                      <label className="d-flex align-items-center gap-3 p-3 bg-white border rounded">
                        <input type="radio" name="tf-correct" className="form-check-input mt-0" onChange={() => setOptions([{text:'True', isCorrect:true}, {text:'False', isCorrect:false}])} />
                        <span className="fw-semibold">True</span>
                      </label>
                      <label className="d-flex align-items-center gap-3 p-3 bg-white border rounded">
                        <input type="radio" name="tf-correct" className="form-check-input mt-0" onChange={() => setOptions([{text:'True', isCorrect:false}, {text:'False', isCorrect:true}])} />
                        <span className="fw-semibold">False</span>
                      </label>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {options.map((opt, index) => (
                        <div key={opt.id} className="d-flex align-items-center gap-3 p-2 bg-white border rounded">
                          <input 
                            type={questionType === 'MCQ' ? 'radio' : 'checkbox'} 
                            name="correctOption"
                            className="form-check-input ms-2"
                            checked={opt.isCorrect}
                            onChange={(e) => handleOptionChange(opt.id, 'isCorrect', e.target.checked)}
                          />
                          <input 
                            type="text" 
                            className="form-control border-0" 
                            placeholder={`Option ${index + 1}`}
                            value={opt.text}
                            onChange={(e) => handleOptionChange(opt.id, 'text', e.target.value)}
                            required
                          />
                          {options.length > 2 && (
                            <button type="button" className="btn btn-light text-danger me-2" onClick={() => removeOption(opt.id)}>
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                <div className="d-flex justify-content-end gap-3">
                  <button type="button" className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => reset()}>Clear</button>
                  <button type="submit" className="btn btn-primary-custom px-5 rounded-pill fw-bold shadow-sm" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Question'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Question List Preview */}
        <div className="col-lg-4">
          <div className="card custom-card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-white border-bottom p-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold m-0">Saved Questions ({questions.length})</h5>
                {selectedQuestionIds.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger d-flex align-items-center gap-1 rounded-pill px-3 py-1 fw-bold shadow-sm"
                    onClick={handleBulkDelete}
                    disabled={deletingBulk}
                  >
                    <FaTrash size={12} />
                    {deletingBulk ? 'Deleting...' : `Delete (${selectedQuestionIds.length})`}
                  </button>
                )}
              </div>
              {questions.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                  <div className="form-check d-flex align-items-center gap-2 mb-0">
                    <input
                      type="checkbox"
                      id="selectAllQuestions"
                      className="form-check-input mt-0"
                      checked={questions.length > 0 && selectedQuestionIds.length === questions.length}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <label htmlFor="selectAllQuestions" className="form-check-label small fw-semibold text-dark user-select-none" style={{ cursor: 'pointer' }}>
                      Select All
                    </label>
                  </div>
                  {selectedQuestionIds.length > 0 && (
                    <span className="small text-muted fw-semibold">
                      {selectedQuestionIds.length} of {questions.length} selected
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="card-body p-0 overflow-auto" style={{ maxHeight: '600px' }}>
              {loading ? (
                <div className="p-5 text-center"><div className="spinner-border text-primary-custom"></div></div>
              ) : questions.length === 0 ? (
                <div className="p-5 text-center text-muted">
                  <p>No questions found in backend.</p>
                </div>
              ) : (
                <ul className="list-group list-group-flush">
                  {questions.map((q, idx) => {
                    const isSelected = selectedQuestionIds.includes(q.id);
                    return (
                      <li key={q.id} className={`list-group-item p-4 ${isSelected ? 'bg-light' : ''}`}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={isSelected}
                              onChange={() => handleToggleSelect(q.id)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                              aria-label={`Select question ${idx + 1}`}
                            />
                            <span className="badge bg-secondary-custom">Q{idx + 1}</span>
                          </div>
                          <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-light text-danger p-1" onClick={() => handleDelete(q.id)} title="Delete Question"><FaTrash /></button>
                          </div>
                        </div>
                        <p className="mb-2 fw-medium">{q.text}</p>
                        <div className="d-flex gap-2 small">
                          <span className="badge bg-light text-dark border">{q.type}</span>
                          <span className="badge bg-light text-success border">+{q.marks}</span>
                          <span className="badge bg-light text-danger border">-{q.negativeMarks}</span>
                          {q.type === 'CODING' && <span className="badge bg-light text-primary border">{(q.testCases || []).length} test cases</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content rounded-4 border-0">
              <div className="modal-header border-bottom-0 p-4">
                <h5 className="modal-title fw-bold">Bulk Import Questions</h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkModal(false)}></button>
              </div>
              <div className="modal-body p-4 pt-0">
                <div className="mb-4">
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-sm btn-success d-flex align-items-center gap-2" onClick={downloadSampleTemplate}>
                      <FaDownload /> MCQ Template (CSV)
                    </button>
                    <button className="btn btn-sm btn-primary d-flex align-items-center gap-2" onClick={downloadCodingTemplate}>
                      <FaDownload /> Coding + MCQ Template (CSV)
                    </button>
                  </div>
                  <small className="d-block text-muted mt-2">MCQ rows: type, text, marks, negativeMarks, difficulty, category, option1-option4, correctOption (1, 2, 3, or 4).</small>
                  <small className="d-block text-muted mt-2">
                    Coding rows: set <strong>type = CODING</strong> and fill <strong>languages</strong> (python, java, cpp, c, javascript; blank = all),
                    <strong> timeLimitSeconds</strong>, <strong>memoryLimitMb</strong>, and the test cases
                    <strong> tc1_input, tc1_output, tc1_hidden (yes/no), tc1_weight</strong>, then tc2_..., tc3_... for more.
                    Leave the option columns empty. At least one test case must be visible (hidden = no).
                    In an input or output cell, write a line break as <code>\n</code> (for example <code>3\n5</code>). MCQ and coding rows can be in the same file.
                  </small>
                </div>
                
                <div className="mb-4">
                  <label className="form-label fw-semibold">Upload CSV / XLSX File</label>
                  <input type="file" className="form-control" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={(e) => setBulkFile(e.target.files[0])} />
                </div>

                {bulkErrors.length > 0 && (
                  <div className="alert alert-danger" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <h6 className="fw-bold">Validation Errors (Skipped Rows)</h6>
                    <ul className="mb-0">
                      {bulkErrors.map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top-0 p-4">
                <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setShowBulkModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary-custom rounded-pill px-4" onClick={handleBulkUpload} disabled={uploadingBulk}>
                  {uploadingBulk ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default QuestionBankPage;
